import { useUser } from "@clerk/clerk-react";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  KeyboardDoubleArrowDown as JumpDownIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Fade,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useRotatingArgusPun } from "../../lib/argus/argus-puns";
import { stripContextPrefix } from "../../lib/argus/argus-text";
import {
  type ArgusUserInfo,
  getArgusUserInfoFromClerk,
  parseSenderBlock,
} from "../../lib/argus/argus-user";
// MarkdownBlock (MuiMarkdown + Monaco) used for all assistant text rendering.
import { ONBOARDING_WELCOME_SENTINEL } from "../../lib/argus/constants";
import { useArgusConnectionContext } from "../../providers/argus-connection-provider";
import { useUserPreferences } from "../../providers/user-preferences-provider";
import { RADIUS_MD } from "../../theme/theme";
import type { ArgusGatewayEvent, ArgusMessage } from "../../types/argus-events";
import { DespleaIcon } from "../icons/desplega-icon";
import { MarkdownBlock } from "../markdown-block";
import { ArgusWelcomeCard } from "../onboarding/argus-welcome-card";
import { ArgusAskUserBlock, type AskUserArgs, type AskUserStatus } from "./argus-ask-user-block";
import { ArgusCompactionDivider } from "./argus-compaction-divider";
import { ArgusNavigateCard } from "./argus-navigate-card";
import { ArgusSenderChip } from "./argus-sender-chip";

export interface ArgusMessageListProps {
  events: ArgusGatewayEvent[];
  seededMessages?: ArgusMessage[];
  optimisticUserMessages?: string[];
  isConnected?: boolean;
  /**
   * True between a user send and the first assistant event for that turn —
   * drives the ephemeral "Argus is thinking" pill rendered below the last
   * block, filling the silent gap before streaming kicks in.
   */
  isAwaitingResponse?: boolean;
  /** True while the transcript fetch is in-flight on session switch. */
  isTranscriptLoading?: boolean;
  /**
   * True when the current session is the onboarding session
   * (`session.config.is_onboarding === true`). Drives the welcome-card
   * render at the top of the transcript + hides the sentinel-bearing first
   * message produced by the BE seed.
   */
  isOnboardingSession?: boolean;
  /**
   * Tool call IDs reported as still in-flight by the gateway snapshot taken
   * on session switch. Used to mark seeded tool blocks as RUNNING when no
   * `tool_execution_end` arrived in the seed (otherwise they'd default to
   * DONE because the seeded shape carries no streaming state).
   */
  hydratedOpenToolCallIds?: readonly string[];
}

/**
 * Argus conversation renderer.
 *
 * Renders a chronological list of message blocks (user, assistant, thinking,
 * tool, ask_user, navigate) derived from the live event stream + any seeded
 * transcript. Each role is a standalone full-width block (except user, which
 * stays as a right-aligned bubble). Supports:
 *   - Standalone collapsible thinking blocks (auto-collapse when streaming finishes)
 *   - Rich tool blocks with pretty-printed args + collapsed output + per-call timing
 *   - Per-turn timing on the first block of each assistant turn
 *   - Logo-based streaming indicator (replaces the ▍ caret + "RUNNING" chip)
 *   - Sticky auto-scroll that disengages when the user scrolls up and
 *     exposes a "jump to latest" button while new content streams in
 *
 * Pencil: Frame 1 (`CA7pb`), Frame 4 (`UCFTf`).
 */

type BlockRole =
  | "user"
  | "assistant"
  | "thinking"
  | "tool"
  | "ask_user"
  | "navigate"
  | "compaction"
  | "stream_idle";

interface CompactionData {
  tokensBefore: number | null;
  summary: string;
  firstKeptEntryId: string | null;
}

interface MessageBlock {
  id: string;
  role: BlockRole;
  label?: string;
  content: string;
  /**
   * Parsed from the gateway's `<!-- argus:sender ... -->` prefix on user
   * messages. `null` for pre-sender-block legacy messages + for non-user
   * roles. Rendered as an author chip above the user bubble.
   */
  sender?: ArgusUserInfo | null;
  isStreaming?: boolean;
  isError?: boolean;
  /**
   * True once the turn this block belongs to has fully ended (turn_end for
   * live events, or the boundary between turns for seeded transcript loads).
   * Drives auto-collapse of thinking + tool blocks.
   */
  turnEnded?: boolean;
  /** Duration of the assistant turn this block belongs to, in ms. Attached to the FIRST block of the turn. */
  turnDurationMs?: number;
  /** Duration of a single tool call (start → end), in ms. */
  toolDurationMs?: number;
  /** Raw args the tool was called with — pretty-printed in the header. */
  toolArgs?: unknown;
  /** Raw result from tool_execution_end — pretty-printed in the collapsible output. */
  toolResult?: unknown;
  askUser?: {
    toolCallId: string;
    args: AskUserArgs;
    status: AskUserStatus;
    /** Populated from the tool result details when the answer has been persisted. */
    answers?: { id: string; value: string }[];
    /** True when the user dismissed/skipped the question. */
    dismissed?: boolean;
  };
  navigate?: {
    path: string;
    label: string;
    reason?: string | null;
  };
  /** Populated when role === "compaction". Drives the transcript divider chip. */
  compaction?: CompactionData;
  /**
   * Populated when role === "stream_idle". DES-147 Phase 3: rendered as an
   * inline warning banner showing how long the stream was idle before the
   * watchdog aborted the session.
   */
  streamIdle?: {
    durationMs: number;
    lastEventType: string | null;
  };
}

/** A group of consecutive thinking + tool blocks collapsed into a single expandable unit. */
interface ChainOfThoughtData {
  id: string;
  blocks: MessageBlock[];
  toolCount: number;
  isStreaming: boolean;
  turnEnded: boolean;
  totalDurationMs?: number;
  /** True when an assistant text block follows this chain — triggers auto-collapse. */
  followedByAssistant: boolean;
}

type RenderItem =
  | { type: "block"; block: MessageBlock }
  | { type: "chain"; chain: ChainOfThoughtData };

interface SeededToolCall {
  id: string;
  name: string;
  arguments: unknown;
}

/**
 * Split pi-ai message content parts into visible text, thinking, and tool
 * calls. The live event stream surfaces tool calls via separate
 * `tool_execution_*` events, so in that path we ignore the `toolCall` parts
 * — the seeded/transcript path uses the `toolCalls` output to reconstruct
 * tool blocks from a loaded conversation.
 */
function extractContent(content: unknown): {
  text: string;
  thinking: string;
  toolCalls: SeededToolCall[];
} {
  if (typeof content === "string") return { text: content, thinking: "", toolCalls: [] };
  if (!Array.isArray(content)) return { text: "", thinking: "", toolCalls: [] };
  const textParts: string[] = [];
  const thinkingParts: string[] = [];
  const toolCalls: SeededToolCall[] = [];
  for (const part of content) {
    if (typeof part === "string") {
      textParts.push(part);
      continue;
    }
    if (!part || typeof part !== "object") continue;
    const partType = (part as { type?: unknown }).type;
    if (partType === "thinking") {
      const t = (part as { thinking?: unknown }).thinking;
      if (typeof t === "string" && t.length > 0) thinkingParts.push(t);
      continue;
    }
    if (partType === "toolCall") {
      const tc = part as { id?: unknown; name?: unknown; arguments?: unknown };
      if (typeof tc.id === "string" && typeof tc.name === "string") {
        toolCalls.push({ id: tc.id, name: tc.name, arguments: tc.arguments });
      }
      continue;
    }
    const maybeText = (part as { text?: unknown }).text;
    if (typeof maybeText === "string") {
      textParts.push(maybeText);
      continue;
    }
    if (typeof partType === "string") {
      textParts.push(`[${partType}]`);
    }
  }
  return {
    text: textParts.join(""),
    thinking: thinkingParts.join("\n\n"),
    toolCalls,
  };
}

/** Flatten a ToolResultMessage's `content` (TextContent[]) into a single string. */
function extractToolResultText(content: unknown): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((p) => {
      if (typeof p === "string") return p;
      if (!p || typeof p !== "object") return "";
      const maybeText = (p as { text?: unknown }).text;
      return typeof maybeText === "string" ? maybeText : "";
    })
    .join("");
}

function messageRole(message: unknown): "user" | "assistant" | "toolResult" | "compaction" {
  const role = (message as { role?: unknown } | null)?.role;
  if (role === "user") return "user";
  if (role === "toolResult") return "toolResult";
  if (role === "compaction") return "compaction";
  return "assistant";
}

/** Human-readable duration ("1.2s" / "850ms" / "1m 3s"). */
function formatDurationMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds - m * 60);
  return `${m}m ${s}s`;
}

/** Safely pretty-print any JSON-ish value. Returns "" for null/undefined/empty. */
function prettyJson(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    // Some tools hand us already-stringified JSON — try to re-format it.
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch {
        return value;
      }
    }
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function foldEvents(
  events: ArgusGatewayEvent[],
  seeded: ArgusMessage[],
  eventReceivedAt: number[],
  optimisticUserMessages: string[] = [],
  selfUser: ArgusUserInfo | null = null,
  hydratedOpenToolCallIds: readonly string[] = [],
): MessageBlock[] {
  const blocks: MessageBlock[] = [];
  // Maps a seeded tool call id → block index so a later toolResult message
  // can populate the matching tool block's result/status.
  const seededToolIndexByCallId = new Map<string, number>();

  // Seeded messages come with `timestamp` fields. We use them to reconstruct
  // turn + tool durations (the live path computes these from receivedAt
  // timestamps, but a reloaded transcript has no receivedAt info).
  //
  // Turn boundaries: each user message starts a new turn; the turn duration
  // is approximated as (lastMessageInTurn.timestamp - userMessage.timestamp).
  // Tool duration is approximated as (toolResult.timestamp - previousMessage.timestamp),
  // which assumes sequential execution but is close enough for display.
  let seedTurnStartAt: number | null = null;
  let seedTurnFirstBlock: MessageBlock | null = null;
  let seedTurnLastAt: number | null = null;
  let seedPrevMessageAt: number | null = null;

  const flushSeedTurn = () => {
    if (seedTurnFirstBlock && seedTurnStartAt != null && seedTurnLastAt != null) {
      seedTurnFirstBlock.turnDurationMs = Math.max(0, seedTurnLastAt - seedTurnStartAt);
    }
    seedTurnFirstBlock = null;
    seedTurnLastAt = null;
  };

  seeded.forEach((message, index) => {
    const rawTs = (message as { timestamp?: unknown }).timestamp;
    const timestamp = typeof rawTs === "number" ? rawTs : null;
    const role = messageRole(message);

    if (role === "compaction") {
      // Archived compaction entry from `_parse_jsonl_to_messages` in
      // be/api/argus/client.py. Emits a divider chip between the
      // summarised history and the kept-recent tail. Breaks any
      // in-progress turn duration tracking.
      flushSeedTurn();
      const compactionMsg = message as {
        summary?: unknown;
        firstKeptEntryId?: unknown;
        tokensBefore?: unknown;
      };
      blocks.push({
        id: `seed-compaction-${index}`,
        role: "compaction",
        content: "",
        compaction: {
          summary: typeof compactionMsg.summary === "string" ? compactionMsg.summary : "",
          firstKeptEntryId:
            typeof compactionMsg.firstKeptEntryId === "string"
              ? compactionMsg.firstKeptEntryId
              : null,
          tokensBefore:
            typeof compactionMsg.tokensBefore === "number" ? compactionMsg.tokensBefore : null,
        },
        turnEnded: true,
      });
      seedPrevMessageAt = timestamp;
      return;
    }

    if (role === "toolResult") {
      const toolCallId = (message as { toolCallId?: unknown }).toolCallId;
      const toolName = (message as { toolName?: unknown }).toolName;
      const isError = Boolean((message as { isError?: unknown }).isError);
      const msgContent = (message as { content?: unknown }).content;
      const details = (message as { details?: unknown }).details;
      if (typeof toolCallId !== "string") return;
      const blockIndex = seededToolIndexByCallId.get(toolCallId);
      if (blockIndex === undefined) return;
      const block = blocks[blockIndex];
      if (!block) return;
      block.isStreaming = false;
      block.isError = isError;
      // Navigate special-case — promote tool block to a navigate card.
      if (toolName === "navigate" && !isError && details) {
        const d = details as {
          path?: string;
          label?: string;
          reason?: string | null;
        };
        if (d.path && d.label) {
          block.role = "navigate";
          block.navigate = {
            path: d.path,
            label: d.label,
            reason: d.reason ?? null,
          };
          block.content = "";
        }
      } else if (block.role === "ask_user" && block.askUser) {
        const d = details as { pending?: boolean; answers?: unknown[] } | undefined;
        const hasAnswers = Array.isArray(d?.answers) && d.answers.length > 0;
        // Only flip status when we have actual answers (or a dismissal).
        // ctx.abort() can cause isError=true with no answers — keep as
        // "pending" in that case so the user can still submit.
        if (hasAnswers && d.answers) {
          const wasDismissed =
            d.answers.length === 1 && (d.answers[0] as { dismissed?: boolean })?.dismissed === true;
          block.askUser.status = isError ? "timed_out" : "answered";
          block.askUser.dismissed = wasDismissed;
          if (!wasDismissed) {
            block.askUser.answers = d.answers as {
              id: string;
              value: string;
            }[];
          }
        }
      } else {
        // Generic tool: prefer `details` (structured payload) else the flattened
        // TextContent children.
        const textContent = extractToolResultText(msgContent);
        block.toolResult = details ?? textContent;
      }
      if (seedPrevMessageAt != null && timestamp != null) {
        block.toolDurationMs = Math.max(0, timestamp - seedPrevMessageAt);
      }
      if (timestamp != null) seedTurnLastAt = timestamp;
      seedPrevMessageAt = timestamp;
      return;
    }

    const { text, thinking, toolCalls } = extractContent(
      (message as { content?: unknown }).content,
    );

    if (role === "user") {
      // User message closes out the previous turn and starts a new one.
      flushSeedTurn();
      seedTurnStartAt = timestamp;

      // Detect crash-recovery answer messages and link them back to the
      // matching ask_user block instead of rendering a raw user bubble.
      // Match against `body` — the gateway prepends `<!-- argus:sender ... -->`
      // which would defeat the `^\[` anchor if we matched raw text.
      const seedBody = text != null ? parseSenderBlock(text).body : "";
      const answerMatch = seedBody.match(/^\[ask_user answers for toolCallId=([^\]]+)\]/);
      if (answerMatch) {
        const answeredToolCallId = answerMatch[1];
        const blockIndex = seededToolIndexByCallId.get(answeredToolCallId);
        if (blockIndex !== undefined) {
          const askBlock = blocks[blockIndex];
          if (askBlock?.askUser) {
            const lines = seedBody.split("\n").slice(1);
            const parsed = lines
              .filter((l: string) => l.startsWith("- "))
              .map((l: string) => {
                const content = l.slice(2);
                if (content === "(skipped)") return { id: "_dismissed", value: "" };
                const sep = content.indexOf(": ");
                return sep >= 0
                  ? { id: content.slice(0, sep), value: content.slice(sep + 2) }
                  : { id: "answer", value: content };
              });
            const wasDismissed = parsed.length === 1 && parsed[0]?.id === "_dismissed";
            askBlock.askUser.status = "answered";
            askBlock.askUser.dismissed = wasDismissed;
            if (!wasDismissed) {
              askBlock.askUser.answers = parsed as {
                id: string;
                value: string;
              }[];
            }
          }
        }
        // Don't render as a user bubble — answers are shown in the ask_user block.
        seedPrevMessageAt = timestamp;
        return;
      }

      if (text) {
        const { sender, body } = parseSenderBlock(text);
        // DES-149: the gateway steers a "[argus:internal-nudge]…" message
        // into the conversation when it detects a tool-call loop. The model
        // must see it, but it's not a real user turn — skip rendering the
        // bubble. Keep the same pattern as the ask_user-answers sentinel above.
        if (/^\[argus:internal-nudge\]/.test(body)) {
          seedPrevMessageAt = timestamp;
          return;
        }
        blocks.push({
          id: `seed-user-${index}`,
          role: "user",
          content: stripContextPrefix(body),
          sender,
        });
      }
      seedPrevMessageAt = timestamp;
      return;
    }

    // Assistant seeded message → emit thinking, text, and tool-call blocks
    // as standalone rows in source order. All thinking + tool blocks are
    // marked turnEnded so they render collapsed on transcript load.
    if (thinking) {
      const block: MessageBlock = {
        id: `seed-thinking-${index}`,
        role: "thinking",
        content: thinking,
        turnEnded: true,
      };
      blocks.push(block);
      if (!seedTurnFirstBlock) seedTurnFirstBlock = block;
    }
    if (text) {
      const block: MessageBlock = {
        id: `seed-assistant-${index}`,
        role: "assistant",
        content: text,
      };
      blocks.push(block);
      if (!seedTurnFirstBlock) seedTurnFirstBlock = block;
    }
    toolCalls.forEach((tc) => {
      if (tc.name === "ask_user") {
        const args = tc.arguments as AskUserArgs;
        // Default to "pending" so the user can still answer a seeded
        // question IF the pending promise is still alive in the gateway's
        // globalThis map. On transcript load the subsequent toolResult
        // walker will flip the status to "answered" / "timed_out" if a
        // result message exists for this toolCallId.
        const block: MessageBlock = {
          id: `seed-ask-user-${tc.id}`,
          role: "ask_user",
          content: "",
          askUser: {
            toolCallId: tc.id,
            args: args ?? { questions: [] },
            status: "pending",
          },
          turnEnded: true,
        };
        blocks.push(block);
        seededToolIndexByCallId.set(tc.id, blocks.length - 1);
        if (!seedTurnFirstBlock) seedTurnFirstBlock = block;
        return;
      }
      const block: MessageBlock = {
        id: `seed-tool-${tc.id}`,
        role: "tool",
        label: tc.name,
        content: "",
        toolArgs: tc.arguments,
        turnEnded: true,
      };
      blocks.push(block);
      seededToolIndexByCallId.set(tc.id, blocks.length - 1);
      if (!seedTurnFirstBlock) seedTurnFirstBlock = block;
    });
    if (timestamp != null) seedTurnLastAt = timestamp;
    seedPrevMessageAt = timestamp;
  });
  // Flush the trailing turn (transcript that doesn't end with a user message).
  flushSeedTurn();

  // Post-process seeded ask_user blocks: if a block is still "pending" but
  // there are subsequent assistant/tool blocks after it, the agent moved on
  // (e.g. replaceMessages wasn't persisted to disk). Force status to
  // "answered" so the form isn't interactive on stale transcripts.
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    if (b.role !== "ask_user" || !b.askUser || b.askUser.status !== "pending") continue;
    const hasSubsequent = blocks
      .slice(i + 1)
      .some(
        (later) => later.role === "assistant" || later.role === "tool" || later.role === "ask_user",
      );
    if (hasSubsequent) {
      // If the tool result has no real answers, treat as dismissed
      const hasAnswers = b.askUser.answers && b.askUser.answers.length > 0;
      b.askUser.status = "answered";
      if (!hasAnswers) b.askUser.dismissed = true;
    }
  }

  // Hydrate seeded tool blocks that the gateway reports as still in-flight.
  // Without this, opening an active session mid-tool shows the block as DONE
  // because the seeded transcript shape carries no streaming state and no
  // `tool_execution_end` event ever made it into the seed. Cross-reference
  // the snapshot from `GET /session/queue/:id` (passed in by the connection
  // hook) and flip those blocks back to RUNNING so the UI matches reality.
  if (hydratedOpenToolCallIds.length > 0) {
    for (const callId of hydratedOpenToolCallIds) {
      const blockIndex = seededToolIndexByCallId.get(callId);
      if (blockIndex === undefined) continue;
      const block = blocks[blockIndex];
      if (!block) continue;
      block.isStreaming = true;
      block.turnEnded = false;
    }
  }

  let currentAssistant: MessageBlock | null = null;
  let currentThinking: MessageBlock | null = null;
  let turnStartAt: number | null = null;
  // Track the first block emitted in the current turn so `turn_end` can
  // attach the total duration to it (rendered as a small footer).
  let turnFirstBlock: MessageBlock | null = null;
  // Every block emitted during the current turn — on turn_end we flip
  // `turnEnded` on all of them so thinking + tool blocks auto-collapse.
  let currentTurnBlocks: MessageBlock[] = [];
  // Track the position of each tool call so tool_execution_end can flip the
  // matching block from "running" to "done".
  const toolIndexByCallId = new Map<string, number>();
  // Per-tool start timestamp — used to compute `toolDurationMs` on the end event.
  const toolStartedAt = new Map<string, number>();

  events.forEach((event, index) => {
    const receivedAt = eventReceivedAt[index];
    switch (event.type) {
      case "turn_start": {
        turnStartAt = receivedAt ?? null;
        turnFirstBlock = null;
        currentTurnBlocks = [];
        break;
      }
      case "message_start":
      case "message_update": {
        const message = (event as { message?: unknown }).message;
        const role = messageRole(message);
        if (role === "toolResult") break;
        const { text, thinking } = extractContent((message as { content?: unknown }).content);
        if (role === "user") {
          const { sender, body } = parseSenderBlock(text);
          // Hide crash-recovery answer messages — answers are shown in
          // the ask_user block, not as a separate user bubble. Check
          // against the stripped body so the sender block doesn't defeat
          // the pattern match.
          if (/^\[ask_user answers for toolCallId=/.test(body)) break;
          // DES-149: loop-detector nudges are steered in as user messages
          // so the model sees them, but they're internal — don't render.
          if (/^\[argus:internal-nudge\]/.test(body)) break;
          blocks.push({
            id: `user-${index}`,
            role: "user",
            content: stripContextPrefix(body),
            sender,
          });
          break;
        }
        // Assistant stream: thinking lands in its OWN standalone block; text
        // lands in a separate assistant block. Both tracked independently so
        // chunks arriving at different times update the right block.
        if (thinking) {
          if (!currentThinking) {
            currentThinking = {
              id: `thinking-${index}`,
              role: "thinking",
              content: thinking,
              isStreaming: true,
            };
            blocks.push(currentThinking);
            currentTurnBlocks.push(currentThinking);
            if (!turnFirstBlock) turnFirstBlock = currentThinking;
          } else {
            currentThinking.content = thinking;
          }
        }
        if (text) {
          if (!currentAssistant) {
            currentAssistant = {
              id: `assistant-${index}`,
              role: "assistant",
              content: text,
              isStreaming: true,
            };
            blocks.push(currentAssistant);
            currentTurnBlocks.push(currentAssistant);
            if (!turnFirstBlock) turnFirstBlock = currentAssistant;
            // Once text starts streaming, thinking for this message is done —
            // flip its streaming flag so the cursor moves from thinking to text.
            if (currentThinking) currentThinking.isStreaming = false;
          } else {
            currentAssistant.content = text;
          }
        }
        break;
      }
      case "message_end": {
        if (currentAssistant) currentAssistant.isStreaming = false;
        if (currentThinking) currentThinking.isStreaming = false;
        currentAssistant = null;
        currentThinking = null;
        break;
      }
      case "tool_execution_start": {
        if (event.toolName === "ask_user") {
          const block: MessageBlock = {
            id: `ask-user-${event.toolCallId}`,
            role: "ask_user",
            content: "",
            isStreaming: true,
            askUser: {
              toolCallId: event.toolCallId,
              args: (event.args as AskUserArgs) ?? { questions: [] },
              status: "pending",
            },
          };
          blocks.push(block);
          currentTurnBlocks.push(block);
          toolIndexByCallId.set(event.toolCallId, blocks.length - 1);
          if (!turnFirstBlock) turnFirstBlock = block;
          break;
        }
        const block: MessageBlock = {
          id: `tool-${event.toolCallId}`,
          role: "tool",
          label: event.toolName,
          content: "",
          isStreaming: true,
          toolArgs: event.args,
        };
        blocks.push(block);
        currentTurnBlocks.push(block);
        toolIndexByCallId.set(event.toolCallId, blocks.length - 1);
        if (receivedAt != null) toolStartedAt.set(event.toolCallId, receivedAt);
        if (!turnFirstBlock) turnFirstBlock = block;
        break;
      }
      case "tool_execution_end": {
        const existingIndex = toolIndexByCallId.get(event.toolCallId);
        if (existingIndex === undefined) break;
        const existing = blocks[existingIndex];
        if (!existing) break;
        existing.isStreaming = false;
        existing.isError = event.isError;
        if (existing.role === "ask_user" && existing.askUser) {
          const resultDetails = (
            event.result as
              | {
                  details?: { pending?: boolean; answers?: unknown[] };
                }
              | undefined
          )?.details;
          const hasAnswers =
            Array.isArray(resultDetails?.answers) && resultDetails.answers.length > 0;
          // Only flip status when we have actual answers (or a dismissal).
          // ctx.abort() can cause isError=true with no answers — keep as
          // "pending" so the user can still submit.
          if (hasAnswers && resultDetails.answers) {
            const wasDismissed =
              resultDetails.answers.length === 1 &&
              (resultDetails.answers[0] as { dismissed?: boolean })?.dismissed === true;
            existing.askUser.status = event.isError ? "timed_out" : "answered";
            existing.askUser.dismissed = wasDismissed;
            if (!wasDismissed) {
              existing.askUser.answers = resultDetails.answers as {
                id: string;
                value: string;
              }[];
            }
          }
        } else if (event.toolName === "navigate" && !event.isError) {
          const resultAny = event.result as
            | {
                details?: {
                  path?: string;
                  label?: string;
                  reason?: string | null;
                };
              }
            | undefined;
          const details = resultAny?.details;
          if (details?.path && details?.label) {
            existing.role = "navigate";
            existing.navigate = {
              path: details.path,
              label: details.label,
              reason: details.reason ?? null,
            };
            existing.content = "";
          } else {
            existing.content = "navigate (missing details)";
          }
        } else {
          existing.toolResult = event.result;
        }
        const startedAt = toolStartedAt.get(event.toolCallId);
        if (startedAt != null && receivedAt != null) {
          existing.toolDurationMs = Math.max(0, receivedAt - startedAt);
        }
        toolStartedAt.delete(event.toolCallId);
        break;
      }
      case "turn_end": {
        if (currentAssistant) currentAssistant.isStreaming = false;
        if (currentThinking) currentThinking.isStreaming = false;
        currentAssistant = null;
        currentThinking = null;
        if (turnFirstBlock && turnStartAt != null && receivedAt != null) {
          turnFirstBlock.turnDurationMs = Math.max(0, receivedAt - turnStartAt);
        }
        // Mark every block emitted during this turn as `turnEnded` so the
        // thinking + tool blocks auto-collapse at the end of the turn.
        for (const b of currentTurnBlocks) {
          b.turnEnded = true;
        }
        currentTurnBlocks = [];
        turnStartAt = null;
        turnFirstBlock = null;
        break;
      }
      case "compaction_end": {
        // Pi-coding-agent auto-compaction finished. Drop a divider chip
        // inline in the transcript so users see when the agent
        // summarised earlier history. `result` is undefined on aborted
        // compactions — skip the marker in that case.
        const result = (event as { result?: unknown }).result as
          | {
              summary?: unknown;
              firstKeptEntryId?: unknown;
              tokensBefore?: unknown;
            }
          | undefined;
        if (!result) break;
        blocks.push({
          id: `compaction-${index}`,
          role: "compaction",
          content: "",
          compaction: {
            summary: typeof result.summary === "string" ? result.summary : "",
            firstKeptEntryId:
              typeof result.firstKeptEntryId === "string" ? result.firstKeptEntryId : null,
            tokensBefore: typeof result.tokensBefore === "number" ? result.tokensBefore : null,
          },
          turnEnded: true,
        });
        break;
      }
      case "stream_idle": {
        // DES-147 Phase 3: the gateway's stream-idle watchdog fired and
        // called `session.abort()`. Drop an inline warning banner so the
        // user knows the session was auto-aborted and they can continue.
        // `durationMs` / `lastEventType` come from the synthetic event
        // emitted by `be/argus-ts/src/stream-idle-watchdog.ts`.
        blocks.push({
          id: `stream-idle-${index}`,
          role: "stream_idle",
          content: "",
          streamIdle: {
            durationMs: event.durationMs,
            lastEventType: event.lastEventType,
          },
          turnEnded: true,
        });
        break;
      }
      default:
        break;
    }
  });

  // Live + seeded user messages are already stripped of the `[Context:
  // ...]` prefix above, so dedupe the optimistic queue against the
  // stripped content and store stripped content for the optimistic
  // bubble too — otherwise the user sees the raw tag during the
  // optimistic window and the dedup fails after the echo lands.
  const existingUserContent = new Set(
    blocks.filter((b) => b.role === "user").map((b) => b.content),
  );
  optimisticUserMessages.forEach((msg, index) => {
    const stripped = stripContextPrefix(msg);
    if (existingUserContent.has(stripped)) return;
    // ask_user answers optimistically submitted through the card surface
    // back as a raw "[ask_user answers for toolCallId=...]" payload. The
    // live + seeded ingest paths already suppress those — mirror the
    // suppression here so the optimistic window doesn't leak the raw tag.
    if (/^\[ask_user answers for toolCallId=/.test(stripped)) return;
    // Optimistic messages haven't round-tripped through the gateway yet,
    // so they carry no sender block. Attach the signed-in user so the chip
    // shows up consistently during the brief optimistic window.
    blocks.push({
      id: `optimistic-user-${index}`,
      role: "user",
      content: stripped,
      sender: selfUser,
    });
  });

  return blocks;
}

/**
 * Group consecutive thinking + tool blocks into chain-of-thought units.
 * Blocks with other roles (user, assistant, ask_user, navigate) break the
 * chain and render standalone. Single-block runs are left unwrapped.
 */
function groupBlocksIntoChains(blocks: MessageBlock[]): RenderItem[] {
  const items: RenderItem[] = [];
  let chain: MessageBlock[] = [];

  const flush = () => {
    if (chain.length === 0) return;
    if (chain.length === 1) {
      items.push({ type: "block", block: chain[0] });
    } else {
      const toolBlocks = chain.filter((b) => b.role === "tool");
      const isStreaming = chain.some((b) => b.isStreaming);
      const turnEnded = chain.every((b) => b.turnEnded);

      // Prefer the turn-level duration if present, otherwise sum tool durations.
      const turnBlock = chain.find((b) => b.turnDurationMs != null);
      let totalDurationMs: number | undefined = turnBlock?.turnDurationMs ?? undefined;
      if (totalDurationMs == null) {
        const toolDurations = toolBlocks
          .map((b) => b.toolDurationMs)
          .filter((d): d is number => d != null);
        if (toolDurations.length > 0) {
          totalDurationMs = toolDurations.reduce((a, b) => a + b, 0);
        }
      }

      items.push({
        type: "chain",
        chain: {
          id: `chain-${chain[0].id}`,
          blocks: chain,
          toolCount: toolBlocks.length,
          isStreaming,
          turnEnded,
          totalDurationMs,
          followedByAssistant: false, // patched below
        },
      });
    }
    chain = [];
  };

  for (const block of blocks) {
    if (block.role === "thinking" || block.role === "tool") {
      chain.push(block);
    } else {
      // If we're flushing a chain and the next block is an assistant text
      // block, mark the chain so it auto-collapses.
      const willFollowWithAssistant = block.role === "assistant";
      flush();
      if (willFollowWithAssistant) {
        const prev = items[items.length - 1];
        if (prev?.type === "chain") prev.chain.followedByAssistant = true;
      }
      items.push({ type: "block", block });
    }
  }
  flush();

  return items;
}

function buildChainSummary(c: ChainOfThoughtData): string {
  if (c.isStreaming && !c.turnEnded) {
    if (c.toolCount === 0) return "Thinking\u2026";
    return `Working\u2026 \u00B7 ${c.toolCount} tool${c.toolCount !== 1 ? "s" : ""}`;
  }
  const durationStr = c.totalDurationMs != null ? formatDurationMs(c.totalDurationMs) : null;
  if (durationStr && c.toolCount > 0) {
    return `Worked for ${durationStr} and used ${c.toolCount} tool${c.toolCount !== 1 ? "s" : ""}`;
  }
  if (durationStr) return `Thought for ${durationStr}`;
  if (c.toolCount > 0) return `Used ${c.toolCount} tool${c.toolCount !== 1 ? "s" : ""}`;
  return "Completed";
}

/**
 * Blinking block cursor used as the "in progress" indicator. Rendered on a
 * new line *below* the streaming content (assistant text, thinking, tool
 * args) so it reads as a terminal-style caret waiting for the next output.
 */
function StreamingCursor() {
  return (
    <Box
      component="span"
      aria-hidden="true"
      sx={{
        display: "inline-block",
        width: "0.55em",
        height: "1em",
        verticalAlign: "text-bottom",
        backgroundColor: "text.primary",
        opacity: 0.85,
        animation: "argus-cursor-blink 1s steps(2, start) infinite",
        "@keyframes argus-cursor-blink": {
          "0%, 49.9%": { opacity: 0.85 },
          "50%, 100%": { opacity: 0 },
        },
      }}
    />
  );
}

const SHIMMER_WORDS = [
  "Brewing ideas…",
  "Consulting the oracle…",
  "Crunching pixels…",
  "Thinking really hard…",
  "Summoning creativity…",
  "Untangling logic…",
  "Polishing thoughts…",
  "Warming up neurons…",
  "Spinning gears…",
  "Channeling wisdom…",
  "Herding electrons…",
  "Composing brilliance…",
  "Loading epiphany…",
  "Decoding the matrix…",
  "Mining insights…",
  "Shuffling synapses…",
  "Calibrating genius…",
  "Cooking up answers…",
  "Wrangling data…",
  "Assembling response…",
  "Chasing butterflies…",
  "Connecting dots…",
  "Tuning frequencies…",
  "Distilling knowledge…",
  "Plotting next move…",
];

/**
 * Shimmer text indicator shown at the bottom of the message list while the
 * agent turn is active but no block is currently streaming. Rotates through
 * fun placeholder words with a gradient sweep animation.
 */
function ShimmerIndicator() {
  const [wordIndex, setWordIndex] = useState(() =>
    Math.floor(Math.random() * SHIMMER_WORDS.length),
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % SHIMMER_WORDS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box
      sx={{
        paddingX: 1.25,
        paddingY: 0.75,
        width: "fit-content",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.8rem",
          fontWeight: 500,
          background: (theme) =>
            `linear-gradient(90deg, ${theme.palette.text.secondary} 0%, ${theme.palette.text.disabled} 40%, ${theme.palette.text.secondary} 60%, ${theme.palette.text.disabled} 100%)`,
          backgroundSize: "200% 100%",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          animation: "argus-shimmer 2s ease-in-out infinite",
          "@keyframes argus-shimmer": {
            "0%": { backgroundPosition: "200% 0" },
            "100%": { backgroundPosition: "-200% 0" },
          },
          transition: "opacity 0.3s ease",
        }}
      >
        {SHIMMER_WORDS[wordIndex]}
      </Typography>
    </Box>
  );
}

/** Small duration footer shown below turn-first blocks and tool blocks. */
function DurationFooter({ ms }: { ms: number }) {
  return (
    <Typography
      variant="caption"
      sx={{
        marginTop: 0.25,
        paddingX: 0.5,
        color: "text.secondary",
        fontSize: "0.7rem",
      }}
    >
      {formatDurationMs(ms)}
    </Typography>
  );
}

/**
 * Standalone thinking block. Expanded while streaming within the current turn
 * so users can watch the reasoning in real time; auto-collapses on turn end
 * or on transcript load. Users can toggle manually at any point.
 */
function ThinkingBlock({ block }: { block: MessageBlock }) {
  // "Active" = currently streaming inside a live, unfinished turn. Seeded
  // blocks (turnEnded=true, isStreaming=false) and blocks whose turn has
  // concluded should be collapsed.
  const active = Boolean(block.isStreaming) && !block.turnEnded;
  const [open, setOpen] = useState(active);
  const prevActive = useRef(active);
  useEffect(() => {
    if (prevActive.current && !active) {
      setOpen(false);
    }
    prevActive.current = active;
  }, [active]);

  return (
    <Box
      sx={{
        width: "100%",
        borderLeft: "2px solid",
        borderColor: "divider",
        backgroundColor: "rgba(0, 0, 0, 0.03)",
        borderRadius: "0 6px 6px 0",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? "Collapse thinking" : "Expand thinking"}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          width: "100%",
          paddingX: 1,
          paddingY: 0.5,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "text.secondary",
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
        }}
      >
        {open ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          thinking
        </Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ padding: 1, paddingTop: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.8rem",
              color: "text.secondary",
              fontStyle: "italic",
              whiteSpace: "pre-wrap",
              lineHeight: 1.5,
            }}
          >
            {block.content}
          </Typography>
          {active && (
            <Box sx={{ marginTop: 0.5 }}>
              <StreamingCursor />
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Rich tool-call block. Fully collapsible — the header is a button toggling
 * a single `<Collapse>` that wraps both args and output. Behavior:
 *   - Expanded while running inside the current turn
 *   - Auto-collapses at turn end (unless it errored)
 *   - Auto-expands on error (even if the user had closed it)
 *   - Seeded/reloaded blocks always start collapsed
 */
function ToolBlock({ block }: { block: MessageBlock }) {
  const argsJson = useMemo(() => prettyJson(block.toolArgs), [block.toolArgs]);
  const resultJson = useMemo(() => prettyJson(block.toolResult), [block.toolResult]);
  const hasResult = resultJson.length > 0;

  // "Active" = currently running inside a live, unfinished turn. Seeded
  // blocks and blocks whose turn has concluded should render collapsed.
  const active = Boolean(block.isStreaming) && !block.turnEnded;
  const [open, setOpen] = useState(active || Boolean(block.isError));
  const prevActive = useRef(active);
  useEffect(() => {
    // Collapse on the active → inactive transition unless this tool errored.
    if (prevActive.current && !active && !block.isError) {
      setOpen(false);
    }
    prevActive.current = active;
  }, [active, block.isError]);
  // Auto-expand on error in case it flipped to error while we were closed.
  useEffect(() => {
    if (block.isError) setOpen(true);
  }, [block.isError]);

  const statusLabel = block.isStreaming ? "RUNNING" : block.isError ? "FAILED" : "DONE";
  const statusColor: "primary" | "error" | "success" = block.isError
    ? "error"
    : block.isStreaming
      ? "primary"
      : "success";

  return (
    <Box
      sx={{
        width: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: `${RADIUS_MD}px`,
        backgroundColor: "action.hover",
        overflow: "hidden",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? "Collapse tool call" : "Expand tool call"}
        sx={{
          width: "100%",
          background: "transparent",
          border: "none",
          padding: 0,
          textAlign: "left",
          cursor: "pointer",
          color: "inherit",
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ paddingX: 1.5, paddingY: 0.75 }}
        >
          {open ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          )}
          <Chip
            label={statusLabel}
            size="small"
            color={statusColor}
            sx={{ fontSize: "10px", height: "18px" }}
          />
          <Typography
            variant="caption"
            sx={{
              fontFamily: "monospace",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {block.label}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {block.toolDurationMs != null && (
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
              {formatDurationMs(block.toolDurationMs)}
            </Typography>
          )}
        </Stack>
      </Box>
      <Collapse in={open} unmountOnExit>
        {argsJson && (
          <Box
            sx={{
              paddingX: 1.5,
              paddingY: 1,
              borderTop: "1px solid",
              borderColor: "divider",
              "& pre": {
                margin: 0,
                fontSize: "0.72rem",
                fontFamily: "monospace",
                color: "text.secondary",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 160,
                overflow: "auto",
              },
            }}
          >
            <pre>{argsJson}</pre>
          </Box>
        )}
        {hasResult && (
          <Box
            sx={{
              paddingX: 1.5,
              paddingY: 1,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "rgba(0, 0, 0, 0.02)",
              "& pre": {
                margin: 0,
                fontSize: "0.72rem",
                fontFamily: "monospace",
                color: "text.primary",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 360,
                overflow: "auto",
              },
            }}
          >
            <pre>{resultJson}</pre>
          </Box>
        )}
        {active && !hasResult && (
          <Box
            sx={{
              paddingX: 1.5,
              paddingY: 1,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <StreamingCursor />
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

/**
 * Chain-of-thought wrapper — groups consecutive thinking + tool blocks into
 * a single collapsible unit with a summary header ("Worked for Xs and used
 * Y tools"). Expanded while streaming; auto-collapses on turn end.
 * Individual blocks inside retain their own expand/collapse for content.
 */
function ChainOfThought({ chain }: { chain: ChainOfThoughtData }) {
  // Closed by default in all cases — while streaming, after a turn ends,
  // and even when a tool failed. The user drives expand/collapse; no
  // auto-open / auto-close effects. The pulsing dot + summary text keep
  // them informed about progress without forcing the panel open.
  const [open, setOpen] = useState(false);
  // Inside the expanded view, show only the most recent 5 blocks by
  // default. A small "Show all" button reveals the full list — not a
  // collapsible, just a one-click toggle.
  const [showAll, setShowAll] = useState(false);
  const shouldCollapse = chain.followedByAssistant || chain.turnEnded;
  const active = !shouldCollapse;

  const summary = buildChainSummary(chain);

  const VISIBLE_RECENT = 5;
  const totalBlocks = chain.blocks.length;
  const hiddenCount = Math.max(0, totalBlocks - VISIBLE_RECENT);
  const visibleBlocks =
    showAll || hiddenCount === 0 ? chain.blocks : chain.blocks.slice(-VISIBLE_RECENT);

  return (
    <Box
      sx={{
        width: "100%",
        borderLeft: "2px solid",
        borderColor: active ? "primary.main" : "divider",
        borderRadius: "0 6px 6px 0",
        transition: "border-color 0.2s ease",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? "Collapse chain of thought" : "Expand chain of thought"}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          width: "100%",
          paddingX: 1,
          paddingY: 0.75,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "text.secondary",
          "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
        }}
      >
        {open ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: "0.75rem" }}>
          {summary}
        </Typography>
        {active && (
          <Box
            sx={{
              marginLeft: 0.5,
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "primary.main",
              animation: "argus-chain-pulse 1.5s ease-in-out infinite",
              "@keyframes argus-chain-pulse": {
                "0%, 100%": { opacity: 0.4 },
                "50%": { opacity: 1 },
              },
            }}
          />
        )}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Stack spacing={1} sx={{ paddingX: 1, paddingTop: 0.5, paddingBottom: 1 }}>
          {hiddenCount > 0 && !showAll && (
            <Box
              component="button"
              type="button"
              onClick={() => setShowAll(true)}
              sx={{
                alignSelf: "flex-start",
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "text.secondary",
                fontSize: "0.7rem",
                fontWeight: 500,
                "&:hover": {
                  color: "primary.main",
                  textDecoration: "underline",
                },
              }}
            >
              Show {hiddenCount} earlier {hiddenCount === 1 ? "step" : "steps"}
            </Box>
          )}
          {visibleBlocks.map((block) => {
            if (block.role === "thinking") return <ThinkingBlock key={block.id} block={block} />;
            if (block.role === "tool") return <ToolBlock key={block.id} block={block} />;
            return null;
          })}
        </Stack>
      </Collapse>
    </Box>
  );
}

function Bubble({
  block,
  onAnswer,
  onDismiss,
  autoNavigate,
  selfUserId,
}: {
  block: MessageBlock;
  onAnswer: (toolCallId: string, answers: unknown[]) => Promise<void>;
  onDismiss: (toolCallId: string) => Promise<void>;
  autoNavigate: boolean;
  /** Clerk id of the signed-in user — drives the "dim my own chip" styling. */
  selfUserId: string | null;
}) {
  if (block.role === "compaction" && block.compaction) {
    return (
      <ArgusCompactionDivider
        tokensBefore={block.compaction.tokensBefore}
        summary={block.compaction.summary}
      />
    );
  }
  if (block.role === "stream_idle" && block.streamIdle) {
    // DES-147 Phase 3: inline warning divider. Mirrors the
    // ArgusCompactionDivider layout (chip between two divider lines) but
    // uses a warning colour so it reads as an error state rather than a
    // neutral marker. Kept inline (no new component) to match the
    // minimal-scope addendum to Phase 3.
    const seconds = Math.round(block.streamIdle.durationMs / 1000);
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          gap: 1,
          my: 0.5,
        }}
      >
        <Box
          sx={{
            flex: 1,
            height: "1px",
            backgroundColor: "warning.main",
            opacity: 0.4,
          }}
        />
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={`Stream idle — session auto-aborted after ${seconds}s of silence. Please continue.`}
          sx={{ fontWeight: 500 }}
        />
        <Box
          sx={{
            flex: 1,
            height: "1px",
            backgroundColor: "warning.main",
            opacity: 0.4,
          }}
        />
      </Box>
    );
  }
  if (block.role === "ask_user" && block.askUser) {
    return (
      <ArgusAskUserBlock
        toolCallId={block.askUser.toolCallId}
        args={block.askUser.args}
        status={block.askUser.status}
        persistedAnswers={block.askUser.answers}
        persistedDismissed={block.askUser.dismissed}
        onSubmit={(answers) => onAnswer(block.askUser!.toolCallId, answers)}
        onDismiss={() => onDismiss(block.askUser!.toolCallId)}
      />
    );
  }
  if (block.role === "navigate" && block.navigate) {
    return (
      <ArgusNavigateCard
        path={block.navigate.path}
        label={block.navigate.label}
        reason={block.navigate.reason}
        alreadyNavigated={autoNavigate}
      />
    );
  }
  if (block.role === "thinking") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <ThinkingBlock block={block} />
        {block.turnDurationMs != null && <DurationFooter ms={block.turnDurationMs} />}
      </Box>
    );
  }
  if (block.role === "tool") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <ToolBlock block={block} />
        {block.turnDurationMs != null && <DurationFooter ms={block.turnDurationMs} />}
      </Box>
    );
  }

  const isUser = block.role === "user";
  if (isUser) {
    const sender = block.sender ?? null;
    // Only treat a message as "other user" when we have positive
    // evidence — i.e. a parsed sender block that doesn't match the
    // signed-in user. Messages with no sender block (legacy / pre-PR
    // sessions, or anything the gateway couldn't parse) default to
    // "self" so we don't accidentally left-align the current user's
    // entire historical chat.
    const isOtherUser = sender != null && !(sender.source === "clerk" && sender.id === selfUserId);
    const isSelf = !isOtherUser;
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: isOtherUser ? "flex-start" : "flex-end",
        }}
      >
        {sender && <ArgusSenderChip sender={sender} isSelf={isSelf} />}
        <Box
          sx={
            isOtherUser
              ? {
                  maxWidth: "85%",
                  padding: 1.5,
                  borderRadius: `${RADIUS_MD}px`,
                  backgroundColor: "transparent",
                  color: "text.primary",
                  border: 1,
                  borderColor: "primary.main",
                }
              : {
                  maxWidth: "85%",
                  padding: 1.5,
                  borderRadius: `${RADIUS_MD}px`,
                  backgroundColor: "primary.main",
                  color: "common.white",
                }
          }
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: "0.875rem",
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
            }}
          >
            {block.content}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Assistant text — full-width, no bubble chrome.
  // Uses MuiMarkdown (MarkdownBlock) for all content — both streaming and
  // completed. Gives consistent Monaco code blocks, proper typography, and
  // polished element styling throughout.
  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Box sx={{ width: "100%", paddingY: 0.5, color: "text.primary" }}>
        <MarkdownBlock content={block.content} />
        {block.isStreaming && (
          <Box sx={{ marginTop: 0.5 }}>
            <StreamingCursor />
          </Box>
        )}
      </Box>
      {block.turnDurationMs != null && <DurationFooter ms={block.turnDurationMs} />}
    </Box>
  );
}

/** Distance-from-bottom (px) within which we treat the user as "stuck to latest". */
const STICKY_THRESHOLD_PX = 48;

export const ArgusMessageList = ({
  events,
  seededMessages = [],
  optimisticUserMessages = [],
  isConnected = false,
  isAwaitingResponse = false,
  isTranscriptLoading = false,
  hydratedOpenToolCallIds = [],
  isOnboardingSession = false,
}: ArgusMessageListProps) => {
  const { user: clerkUser } = useUser();
  // The signed-in user — attached to optimistic bubbles so the sender chip
  // shows up before the gateway round-trip completes. Also used to dim
  // "my" chips when parsed senders come back.
  const selfUser = useMemo(() => getArgusUserInfoFromClerk(clerkUser), [clerkUser]);
  const selfUserId = selfUser && selfUser.source === "clerk" ? selfUser.id : null;

  // Receive-timestamp buffer keyed by the event index. Growing only — we
  // append `Date.now()` for each new event the first time we see it so
  // turn timings survive re-renders.
  const receivedAtRef = useRef<number[]>([]);
  if (receivedAtRef.current.length < events.length) {
    const now = Date.now();
    for (let i = receivedAtRef.current.length; i < events.length; i++) {
      receivedAtRef.current.push(now);
    }
  } else if (receivedAtRef.current.length > events.length) {
    // Session switch resets the events array — drop stale timestamps too.
    receivedAtRef.current = receivedAtRef.current.slice(0, events.length);
  }

  const blocks = useMemo(
    () =>
      foldEvents(
        events,
        seededMessages,
        receivedAtRef.current,
        optimisticUserMessages,
        selfUser,
        hydratedOpenToolCallIds,
      ),
    [events, seededMessages, optimisticUserMessages, selfUser, hydratedOpenToolCallIds],
  );

  // When the active session is the onboarding session, the BE seeds the
  // first assistant message with `ONBOARDING_WELCOME_SENTINEL\n...` — we
  // render a rich welcome card in its place and drop the sentinel message
  // from the normal block stream so it doesn't show up as a plain chat
  // bubble. The three-way guard (index 0 + is_onboarding + sentinel
  // prefix) keeps the blast radius minimal: a future user message that
  // happens to echo the literal sentinel can't hijack the UI.
  const displayBlocks = useMemo(() => {
    if (!isOnboardingSession) return blocks;
    if (blocks.length === 0) return blocks;
    const first = blocks[0];
    if (first.content?.startsWith(`${ONBOARDING_WELCOME_SENTINEL}\n`)) {
      return blocks.slice(1);
    }
    return blocks;
  }, [blocks, isOnboardingSession]);

  const renderItems = useMemo(() => groupBlocksIntoChains(displayBlocks), [displayBlocks]);

  // True when the agent has an open turn (turn_start without a matching
  // turn_end). Used to show a global progress indicator at the bottom of
  // the message list, independent of individual block streaming states.
  const isAgentWorking = useMemo(() => {
    let open = 0;
    for (const e of events) {
      if (e.type === "turn_start") open++;
      else if (e.type === "turn_end") open--;
    }
    return open > 0;
  }, [events]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const [showFollowButton, setShowFollowButton] = useState(false);
  const { answerQuestion, dismissQuestion } = useArgusConnectionContext();
  const { preferences } = useUserPreferences();
  const location = useLocation();
  const isOnArgusPage = location.pathname.startsWith("/argus");
  const autoNavigate = isOnArgusPage ? false : Boolean(preferences.argusAutoNavigate);
  const pun = useRotatingArgusPun();

  const isAtBottom = useCallback((el: HTMLElement) => {
    return el.scrollHeight - el.scrollTop - el.clientHeight < STICKY_THRESHOLD_PX;
  }, []);

  const scrollToBottom = useCallback((smooth = true) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    stickToBottomRef.current = true;
    setShowFollowButton(false);
  }, []);

  // Track user scroll intent — once the user scrolls up past the threshold
  // we disengage auto-follow and surface a "jump to latest" button. The
  // button is purely reactive to scroll position: if the transcript doesn't
  // overflow no scroll event ever fires so the button stays hidden.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const atBottom = isAtBottom(el);
      stickToBottomRef.current = atBottom;
      setShowFollowButton(!atBottom);
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [isAtBottom]);

  // Auto-scroll policy on new content: while the user is "stuck to bottom",
  // keep pinning them there as new content streams in. If they've scrolled
  // up, re-evaluate whether the transcript actually overflows — only show
  // the jump-down affordance when scrolling is actually possible.
  //
  // `events.length` grows on every SSE tick (message_update, tool_execution_*,
  // turn_*) and `isAwaitingResponse` flips on user send. Both are trigger
  // deps — the effect body reads refs, not these values. Biome's
  // useExhaustiveDependencies can't model this pattern, so we suppress;
  // without the dep the effect would run once on mount and never auto-scroll
  // during streaming.
  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger deps for the sticky auto-scroll effect
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      return;
    }
    const overflows = el.scrollHeight > el.clientHeight + 1;
    setShowFollowButton(overflows);
  }, [events.length, isAwaitingResponse]);

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        ref={scrollRef}
        role="log"
        aria-live="polite"
        aria-label="Argus conversation"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          // Reduce outer padding on small viewports so chat bubbles get
          // the full width of the screen.
          padding: { xs: 1, md: 2 },
        }}
      >
        {isTranscriptLoading && blocks.length === 0 ? (
          <Stack
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%", textAlign: "center", paddingX: 4 }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Loading conversation…
            </Typography>
          </Stack>
        ) : isOnboardingSession ? (
          // Onboarding session: always show the welcome card at the top of
          // the transcript, followed by any additional blocks (agent
          // reasoning + user replies) once the conversation starts.
          <Box sx={{ maxWidth: 820, marginX: "auto", width: "100%" }}>
            <Stack spacing={1.5}>
              <ArgusWelcomeCard />
              {renderItems.map((item) =>
                item.type === "chain" ? (
                  <ChainOfThought key={item.chain.id} chain={item.chain} />
                ) : (
                  <Bubble
                    key={item.block.id}
                    block={item.block}
                    onAnswer={answerQuestion}
                    onDismiss={dismissQuestion}
                    autoNavigate={autoNavigate}
                    selfUserId={selfUserId}
                  />
                ),
              )}
              {(isAwaitingResponse || isAgentWorking) && <ShimmerIndicator />}
            </Stack>
          </Box>
        ) : blocks.length === 0 ? (
          <Stack
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%", textAlign: "center", paddingX: 4 }}
          >
            <DespleaIcon sx={{ fontSize: 48, color: "primary.main" }} />
            <Typography variant="h6" sx={{ color: "text.primary", fontWeight: 500 }}>
              {isConnected ? "Argus is listening" : "No messages yet"}
            </Typography>
            <Fade in key={pun} timeout={{ enter: 500, exit: 300 }}>
              <Typography
                variant="body2"
                sx={{
                  maxWidth: 420,
                  minHeight: 40,
                  background: (theme) =>
                    `linear-gradient(90deg, ${theme.palette.text.secondary} 0%, ${theme.palette.text.disabled} 50%, ${theme.palette.text.secondary} 100%)`,
                  backgroundSize: "200% 100%",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  animation: "argus-pun-shimmer 3s ease-in-out infinite",
                  "@keyframes argus-pun-shimmer": {
                    "0%": { backgroundPosition: "200% 0" },
                    "100%": { backgroundPosition: "-200% 0" },
                  },
                }}
              >
                {pun}
              </Typography>
            </Fade>
          </Stack>
        ) : (
          <Box sx={{ maxWidth: 820, marginX: "auto", width: "100%" }}>
            <Stack spacing={1.5}>
              {renderItems.map((item) =>
                item.type === "chain" ? (
                  <ChainOfThought key={item.chain.id} chain={item.chain} />
                ) : (
                  <Bubble
                    key={item.block.id}
                    block={item.block}
                    onAnswer={answerQuestion}
                    onDismiss={dismissQuestion}
                    autoNavigate={autoNavigate}
                    selfUserId={selfUserId}
                  />
                ),
              )}
              {(isAwaitingResponse || isAgentWorking) && <ShimmerIndicator />}
            </Stack>
          </Box>
        )}
      </Box>
      {showFollowButton && blocks.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 12,
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<JumpDownIcon />}
            onClick={() => scrollToBottom(true)}
            sx={{
              pointerEvents: "auto",
              borderRadius: 999,
              textTransform: "none",
              boxShadow: 3,
            }}
          >
            Jump to latest
          </Button>
        </Box>
      )}
    </Box>
  );
};
